/**
 * Design tokens derived from the Figma "SchoolCash Web App" design system.
 * Light mode source:  node 445:72653
 * Dark mode source:   node 469:53422
 *
 * Only the semantic colors that the implemented views actually need are mapped
 * here. The shared scales (spacing, radius, typography) are mode-independent and
 * live below the palette definitions.
 */

export interface AppPalette {
  /** Page background (general/background). */
  background: string;
  mutedBackground: string;
  /** Default text color (general/foreground). */
  foreground: string;
  /** Surface for cards / containers (card/card). */
  card: string;

  /** Primary action color (general/primary) — solid buttons. */
  primary: string;
  /** Text on primary surfaces (general/primary foreground). */
  primaryForeground: string;
  /** Primary hover state (unofficial/primary hover). */
  primaryHover: string;

  /** Secondary surface (general/secondary). */
  secondary: string;
  /** Text on secondary surfaces (general/secondary foreground). */
  secondaryForeground: string;

  /** De-emphasized text — labels, captions (general/muted foreground). */
  mutedForeground: string;

  /** Default border (general/border). */
  border: string;
  /** Subtle hairline border — header divider (unofficial/border 1). */
  borderSubtle: string;
  /** Stronger border — outlined buttons / inputs (unofficial/border 3). */
  borderStrong: string;

  /** Input field background (general/input). */
  input: string;
  /** Focus ring color (focus/ring). */
  ring: string;

  /** Destructive / error color (general/destructive). */
  destructive: string;
  /** Text on destructive surfaces (general/destructive foreground). */
  destructiveForeground: string;

  /** Link / informational accent (blue/500). */
  link: string;
  /** Brand accent green (parent/500) — used for highlighted copy. */
  accent: string;
  /** Tinted surface behind the login card (parent/100). */
  accentSurface: string;
  /** Tinted surface for informational callouts (active/100). */
  infoSurface: string;
  /** Tinted surface for destructive/error callouts. */
  destructiveSurface: string;
}

export const lightPalette: AppPalette = {
  background: '#ffffff',
  mutedBackground: '#f9fafb',
  foreground: '#010514',
  card: '#ffffff',

  primary: '#0e162a',
  primaryForeground: '#f9fafb',
  primaryHover: '#343f54',

  secondary: '#f3f4f6',
  secondaryForeground: '#0e162a',

  mutedForeground: '#485366',

  border: '#e4e6ec',
  borderSubtle: '#f3f4f6',
  borderStrong: '#d0d4dc',

  input: '#ffffff',
  ring: '#d0d4dc',

  destructive: '#e51e1d',
  destructiveForeground: '#ffffff',

  link: '#3e9df3',
  accent: '#26a494',
  accentSurface: '#d2f5ed',
  infoSurface: '#dbeffe',
  destructiveSurface: '#fde8e8',
};

export const darkPalette: AppPalette = {
  background: '#010514',
  mutedBackground: '#010514',
  foreground: '#f9fafb',
  card: '#010514',

  primary: '#f3f4f6',
  primaryForeground: '#010514',
  primaryHover: '#d0d4dc',

  secondary: '#1c2739',
  secondaryForeground: '#f3f4f6',

  mutedForeground: '#d0d4dc',

  border: '#343f54',
  borderSubtle: '#0e162a',
  borderStrong: '#343f54',

  input: 'rgba(255, 255, 255, 0.05)',
  ring: '#343f54',

  destructive: '#841918',
  destructiveForeground: '#ffffff',

  link: '#3e9df3',
  accent: '#26a494',
  accentSurface: '#1c2739',
  infoSurface: '#1c2739',
  destructiveSurface: '#3d1818',
};

export type ThemeMode = 'light' | 'dark';

/** Roles selectable in the header that re-tint the brand accent. */
export type AppRole = 'parent' | 'organization';

/**
 * Accent overrides layered on top of the base palette depending on the role
 * picked in the header. `parent` keeps the base palette accent; `organization`
 * re-tints the accent (and, in light mode, its surface) to a blue brand color.
 */
export const roleAccents: Record<
  AppRole,
  Record<ThemeMode, Partial<Pick<AppPalette, 'accent' | 'accentSurface'>>>
> = {
  parent: {
    light: {},
    dark: {},
  },
  organization: {
    light: { accent: '#2155ad', accentSurface: '#dbeffe' },
    dark: { accent: '#c0e4fd' },
  },
};

/** Border radius scale (px) from the design system. */
export const radii = {
  sm: 4,
  lg: 8,
  '2xl': 16,
  '3xl': 24,
  full: 9999,
} as const;

/** Spacing scale (px) from the design system. */
export const spacing = {
  '3xs': 2,
  '2xs': 4,
  xs: 8,
  sm: 12,
  md: 16,
  xl: 24,
  '3xl': 40,
} as const;

export const fontFamily =
  "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
